import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, MonitorSmartphone, RefreshCw, Globe, Nfc,
  MessageSquare, Layers, Sparkles, Headset,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { HeroMockup } from "@/components/HeroMockup";
import { FinalCta } from "@/components/FinalCta";
import { BlogTeaser } from "@/components/BlogTeaser";
import { Reviews } from "@/components/Reviews";
import { useLanguage } from "../i18n";

const SERVICES = [
  {
    icon: MonitorSmartphone,
    title: "Realizzazione siti web",
    desc: "Siti web moderni e professionali, progettati per presentare chiaramente la tua attività e aiutare i clienti a scoprire i tuoi servizi.",
    cta: "Richiedi il tuo sito web",
    to: "/siti-web",
    big: false,
  },
  {
    icon: RefreshCw,
    title: "Redesign del tuo sito web",
    desc: "Dai al tuo sito esistente una struttura più chiara, un aspetto più moderno e un'esperienza migliore su ogni dispositivo.",
    cta: "Richiedi il redesign",
    to: "/contatti",
    big: false,
  },
  {
    icon: Globe,
    title: "Presenza digitale",
    desc: "Soluzioni digitali pratiche che aiutano le piccole imprese a presentarsi online in modo coerente e professionale.",
    cta: "Prenota una consulenza",
    to: "/consulenze",
    big: false,
  },
  {
    icon: Nfc,
    title: "Recensioni NFC",
    desc: "Carte e adesivi NFC semplici da usare: i clienti raggiungono la tua pagina delle recensioni Google con un tocco dello smartphone.",
    cta: "Richiedi informazioni",
    to: "/recensioni-nfc",
    big: false,
  },
];

const STRENGTHS = [
  { icon: MessageSquare, title: "Chiarezza", desc: "Spieghiamo ogni fase del progetto con un linguaggio semplice, senza complicazioni inutili." },
  { icon: Layers, title: "Soluzioni personalizzate", desc: "Partiamo dalle tue esigenze per individuare la soluzione più adatta al tuo progetto." },
  { icon: Sparkles, title: "Design e funzionalità", desc: "Uniamo estetica, usabilità e tecnologia per creare esperienze digitali efficaci." },
  { icon: Headset, title: "Supporto diretto", desc: "Potrai confrontarti con noi per valutare le tue idee, chiarire i tuoi dubbi e pianificare i prossimi passi." },
];

const PROJECTS = [
  {
    img: "/assets/hero-alsera.jpg",
    name: "Vetrina Moderna",
    type: "Sito vetrina",
    desc: "Concept di sito vetrina responsive: la stessa esperienza curata su desktop e smartphone.",
  },
  {
    img: "https://images.unsplash.com/photo-1645021785273-140b720bb63f?crop=entropy&cs=srgb&fm=jpg&q=85",
    name: "Studio Aurora",
    type: "Salone & beauty",
    desc: "Concept di sito per un salone di bellezza: servizi in evidenza, prenotazione semplice e contatti immediati.",
  },
  {
    img: "https://images.unsplash.com/photo-1530435460869-d13625c69bbf?crop=entropy&cs=srgb&fm=jpg&q=85",
    name: "Bottega del Gusto",
    type: "Ristorante & café",
    desc: "Concept di sito per un ristorante o café: menù, informazioni e contatto diretto in primo piano.",
  },
];

const HOW_IT_WORKS = [
  { num: "01", title: "Ascolto", desc: "Conosciamo la tua attività, i tuoi obiettivi e le persone che vuoi raggiungere." },
  { num: "02", title: "Proposta", desc: "Definiamo insieme l'ambito del progetto e prepariamo un preventivo su misura." },
  { num: "03", title: "Design e sviluppo", desc: "Sviluppiamo la soluzione concordata, curando design e contenuti." },
  { num: "04", title: "Revisione", desc: "Esamini il lavoro e condividi il tuo feedback nell'ambito concordato." },
  { num: "05", title: "Consegna", desc: "Completiamo il lavoro e ti spieghiamo i passi successivi." },
];

const BENEFITS = [
  "Un confronto dedicato alle tue esigenze",
  "Un primo orientamento sulle possibili soluzioni",
  "La possibilità di fare domande e chiarire i tuoi dubbi",
  "Nessun obbligo di acquistare un servizio",
];

const FAQ = [
  { q: "Con quali tipi di attività lavorate?", a: "Lavoriamo con attività indipendenti e piccole imprese: ristoranti e café, saloni di bellezza, negozi locali, piccoli rivenditori e fornitori di servizi in Svizzera." },
  { q: "Potete ridisegnare il mio sito esistente?", a: "Sì. Analizziamo il tuo sito attuale e lo rinnoviamo nella struttura, nell'aspetto e nell'esperienza su tutti i dispositivi." },
  { q: "Il mio sito funzionerà su smartphone e tablet?", a: "Sì, ogni sito che realizziamo è progettato per funzionare bene su schermi di qualsiasi dimensione: smartphone, tablet e computer." },
  { q: "Quanto costa realizzare un sito web?", a: "Il costo dipende dalle caratteristiche del progetto: numero di pagine, funzionalità e livello di personalizzazione. Per questo offriamo sempre un preventivo gratuito e senza impegno, costruito sulle tue reali esigenze." },
  { q: "Quanto tempo occorre per completare un progetto?", a: "I tempi variano in base alla complessità del sito e ai materiali necessari. Dopo la prima consulenza ti forniamo una stima realistica e condivisa, aggiornata passo dopo passo." },
  { q: "Come posso iniziare?", a: "Scrivici tramite il modulo di contatto o prenota una consulenza: parleremo della tua attività e dei prossimi passi, senza impegno." },
  { q: "Posso richiedere un preventivo senza impegno?", a: "Sì. Raccontaci il tuo progetto tramite il modulo di contatto o prenotando una consulenza: ti prepareremo un preventivo chiaro e dettagliato, senza impegno." },
  { q: "È possibile modificare o ampliare il sito in futuro?", a: "Sì, i siti che realizziamo sono pensati per crescere con la tua attività: potrai aggiungere pagine, funzionalità e contenuti anche dopo la pubblicazione." },
];

export default function Home() {
  const { t } = useLanguage();
  const heroLines = [
    { text: t("Il tuo business merita"), accent: false },
    { text: t("una presenza online"), accent: true },
    { text: t("migliore."), accent: false },
  ];

  return (
    <>
      <Seo
        title={t("Alsera Studio — Siti web moderni per piccole imprese in Svizzera")}
        description={t("Alsera Studio crea siti web moderni, redesign e soluzioni digitali pratiche per piccole imprese e attività indipendenti in Svizzera. Richiedi un preventivo senza impegno.")}
      />

      {/* HERO */}
      <section className="noise-overlay relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-24" data-testid="hero-section">
        <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-acqua/60 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute top-40 right-0 h-72 w-72 rounded-full bg-salvia/25 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow"
              data-testid="hero-eyebrow"
            >
              {t("Studio digitale per piccole imprese in Svizzera")}
            </motion.p>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-antracite sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {heroLines.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.accent ? "text-elettrico italic" : ""}`}
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.12 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-antracite/75 sm:text-lg"
              data-testid="hero-subtitle"
            >
              {t("Siti web moderni e soluzioni digitali pratiche, pensati per le piccole imprese in Svizzera.")}
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-elettrico"
              data-testid="hero-tagline"
            >
              <Sparkles className="h-4 w-4" />
              {t("La tua visione digitale, la nostra esperienza.")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link to="/contatti" className="btn-primary" data-testid="hero-cta-booking">
                {t("Richiedi un preventivo")}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#portfolio" className="btn-secondary" data-testid="hero-cta-services">
                {t("Esplora i nostri lavori")}
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-5 flex items-center gap-2 text-sm font-medium text-antracite/60"
              data-testid="hero-reassurance"
            >
              <Check className="h-4 w-4 text-petrolio" />
              {t("Parliamo del tuo progetto, senza impegno.")}
            </motion.p>
          </div>
          <HeroMockup />
        </div>
      </section>

      <Marquee />

      {/* SERVIZI */}
      <section className="py-24 sm:py-28" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Servizi")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Soluzioni digitali pensate per te.")}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-antracite/70 sm:text-lg">
              {t("Ogni progetto è diverso. Per questo offriamo servizi flessibili, progettati intorno ai tuoi obiettivi.")}
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className={s.big ? "lg:row-span-2" : ""}>
                <div
                  className={`group card-soft flex h-full flex-col justify-between p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10 sm:p-10 ${s.big ? "bg-gradient-to-br from-white to-acqua/40" : ""}`}
                  data-testid={`service-card-${i}`}
                >
                  <div>
                    <div className="mb-6 inline-flex rounded-2xl bg-acqua p-3.5 text-petrolio transition-colors duration-500 group-hover:bg-petrolio group-hover:text-avorio">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-bold tracking-tight text-antracite sm:text-2xl">{t(s.title)}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-antracite/70 sm:text-base">{t(s.desc)}</p>
                  </div>
                  <Link
                    to={s.to}
                    data-testid={`service-cta-${i}`}
                    className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-petrolio transition-all duration-300 hover:gap-3.5"
                  >
                    {t(s.cta)}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="py-24 sm:py-28" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Come funziona")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Un processo chiaro, dall'idea alla consegna.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-6">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.08}>
                <div className="relative" data-testid={`how-step-${i}`}>
                  <span className="font-display text-4xl font-extrabold text-elettrico/25">{s.num}</span>
                  <h3 className="mt-3 text-base font-bold tracking-tight text-antracite sm:text-lg">{t(s.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-antracite/70">{t(s.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PERCHÉ ALSERA */}
      <section className="noise-overlay bg-acqua/45 py-24 sm:py-28" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Perché Alsera Studio")}</p>
            <h2 className="mt-4 max-w-3xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("La tecnologia è uno strumento. Il tuo successo è l'obiettivo.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2">
            {STRENGTHS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="flex gap-5" data-testid={`strength-${i}`}>
                  <span className="font-display text-sm font-extrabold text-salvia">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="mb-2 flex items-center gap-3">
                      <s.icon className="h-5 w-5 text-petrolio" />
                      <h3 className="text-lg font-bold tracking-tight text-antracite">{t(s.title)}</h3>
                    </div>
                    <p className="max-w-md text-sm leading-relaxed text-antracite/70 sm:text-base">{t(s.desc)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECENSIONI */}
      <Reviews />

      {/* PORTFOLIO */}
      <section id="portfolio" className="scroll-mt-24 py-24 sm:py-28" data-testid="portfolio-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">{t("Portfolio")}</p>
              <h2 className="mt-4 text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
                {t("Le idee prendono forma.")}
              </h2>
              <p className="mt-4 max-w-xl text-base text-antracite/70">
                {t("Una selezione di concept dimostrativi: esempi di come potrebbe prendere vita il tuo progetto digitale.")}
              </p>
            </div>
            <Link to="/siti-web" className="btn-secondary" data-testid="portfolio-view-all">
              {t("Vedi tutti i progetti")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PROJECTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <article
                  className="group card-soft overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10"
                  data-testid={`project-card-${i}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={p.img}
                      alt={`${p.name} — ${t(p.type)}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-avorio/90 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-petrolio backdrop-blur">
                      {t("Concept — design dimostrativo")}
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-petrolio/70">{t(p.type)}</p>
                    <h3 className="mt-1.5 text-lg font-bold tracking-tight text-antracite">{p.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(p.desc)}</p>
                    <Link
                      to="/siti-web"
                      data-testid={`project-link-${i}`}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-petrolio transition-all duration-300 hover:gap-3.5"
                    >
                      {t("Visualizza il progetto")}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRENOTAZIONE */}
      <section className="py-24 sm:py-28" data-testid="booking-teaser-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="card-soft noise-overlay grid items-center gap-10 overflow-hidden p-8 sm:p-14 lg:grid-cols-[1.1fr_0.9fr]">
            <Reveal>
              <p className="eyebrow">{t("Consulenza e prenotazione")}</p>
              <h2 className="mt-4 text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
                {t("Il tuo progetto merita il giusto spazio.")}
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-antracite/70">
                {t("Hai un'idea, vuoi creare un sito web o desideri migliorare la tua presenza online? Prenota una consulenza personalizzata e scopriamo insieme le opportunità più adatte a te.")}
              </p>
              <div className="mt-8">
                <Link to="/prenota" className="btn-primary" data-testid="booking-teaser-cta">
                  {t("Prenota il tuo appuntamento")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="mt-4 text-sm text-antracite/55">{t("Scegli il giorno e l'orario più comodi per te.")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="space-y-4">
                {BENEFITS.map((b, i) => (
                  <li key={b} className="flex items-start gap-3 rounded-2xl bg-acqua/50 p-4" data-testid={`booking-benefit-${i}`}>
                    <span className="mt-0.5 rounded-full bg-petrolio p-1 text-avorio">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm font-medium leading-relaxed text-antracite/80">{t(b)}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24 sm:pb-28" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">FAQ</p>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Le risposte alle tue domande.")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-salvia/40">
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="text-left text-sm font-bold text-antracite hover:text-petrolio sm:text-base"
                  >
                    {t(f.q)}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-antracite/70 sm:text-base">
                    {t(f.a)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <BlogTeaser />

      <FinalCta />
    </>
  );
}
